import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { Plus } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'What does BeLoud actually help me do?',
    answer: 'It helps you find stronger angles, prepare LinkedIn posts and write better comments without starting from a blank page.',
  },
  {
    question: 'How does the trial work?',
    answer: 'You get 3 days to try the workflow, set up your profile and see whether the drafts sound useful enough to keep using it.',
  },
  {
    question: 'Who is it for?',
    answer: 'Founders, consultants, agencies and operators who know LinkedIn matters but do not want to sound generic.',
  },
  {
    question: 'What languages are supported?',
    answer: 'English, Italian and Polish. The goal is native-feeling writing, not translated filler.',
  },
  {
    question: 'What happens after signup?',
    answer: 'You set up your profile, define the voice and start preparing posts or comments from real LinkedIn context.',
  },
];

function AccordionItem({ item, isOpen, onToggle, index }: {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.06, duration: 0.35 }}
      className="overflow-hidden border-2 border-border bg-card shadow-[5px_5px_0px_hsl(var(--foreground))]"
    >
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between p-5 text-left transition-colors hover:bg-muted/50"
      >
        <span className="pr-4 font-black">{item.question}</span>
        <motion.div animate={{ rotate: isOpen ? 45 : 0 }} transition={{ duration: 0.2 }} className="shrink-0">
          <Plus className="h-5 w-5 text-muted-foreground" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="px-5 pb-5 leading-6 text-muted-foreground">{item.answer}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="px-4 py-20 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="mb-12 text-center"
        >
          <p className="brutalist-label mb-3 inline-flex">FAQ</p>
          <h2 className="text-4xl font-black tracking-[-0.04em]">Quick answers. No pitch theatre.</h2>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem
              key={faq.question}
              item={faq}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
